import { formatISO9075 } from 'date-fns';

function escapeDelimiter(value: unknown, delimiter: string): unknown {
  const escapeStrings = ['"', delimiter];
  const needsEscapeRegEx = RegExp(escapeStrings.join('|'), 'g');
  if (needsEscapeRegEx.test(value as string)) {
    const quoteEscapedValue = (value as string).toString().replace(/"/g, '""');
    return `"${quoteEscapedValue}"`;
  }
  return value;
}

// regex that checks if a string could be an integer or number with decimal:
export const isNumber = (item: unknown): boolean =>
  /^-?\d+$/.test(item as string) || /^-?\d+\.\d+$/.test(item as string);

export const isValidPortalIsoString = (item: unknown): boolean => {
  const dateObject = new Date(item as string);
  return !Number.isNaN(dateObject.valueOf()) && dateObject.toISOString() === item;
};

export const formatToIso9075 = (item: unknown): string => {
  return formatISO9075(new Date(item as string), { format: 'extended', representation: 'date' });
};

export const formatDataPoint = (item: unknown): unknown => {
  // booleans
  if (typeof item === 'boolean') return item ? 'true' : 'false';
  // empty
  if (item === null) return 'null';
  // integer or number with decimal
  if (isNumber(item)) return Number(item);
  // Portal date formats
  const isDateObject = Object.prototype.toString.call(item) === '[object Date]';
  if (item !== 0 && (isDateObject || isValidPortalIsoString(item))) {
    return formatToIso9075(item);
  }
  // everything else
  return item;
};

interface CsvOptions {
  data?: Record<string, unknown>[] | null;
  columnDelimiter?: string;
  lineDelimiter?: string;
  doNotFormatTheseFields?: string[];
  keys?: string[];
}

export function convertArrayOfObjectsToCSV({
  data = null,
  columnDelimiter = ',',
  lineDelimiter = '\n',
  doNotFormatTheseFields = [],
  keys = Object.keys(data?.[0] ?? {}),
}: CsvOptions): string | null {
  if (!data?.length) return null;
  const formatRow = (row: Record<string, unknown>) =>
    keys
      .map((key) =>
        escapeDelimiter(
          doNotFormatTheseFields.includes(key) ? row[key] : formatDataPoint(row[key]),
          columnDelimiter,
        ),
      )
      .join(columnDelimiter);
  // empty spaces in column names must be removed
  const columnNameData = keys.reduce<Record<string, string>>(
    (acc, key) => ({ ...acc, [key]: key.replace(/\s/g, '') }),
    {},
  );

  return [columnNameData, ...data].map(formatRow).join(lineDelimiter);
}

interface DownloadCsvArgs {
  filename: string;
  objArray?: Record<string, unknown>[];
  csvData?: string | null;
}

export function downloadCSV(args: DownloadCsvArgs): void {
  const { filename, objArray } = args;

  if (!filename && !(objArray || args.csvData)) return;

  const csvData =
    args.csvData ||
    convertArrayOfObjectsToCSV({
      data: objArray,
    });

  // encodeURI function does not encode the following special characters: ~!@#$&*()=:/,;?+'
  // because Excel / spreadsheet programs do not decode from a URL, a string URI cannot be used
  // instead, create a blob and place the csv within the blob
  const csvFile = new Blob([csvData as string], { type: 'text/csv' });

  const link = document.createElement('a');

  if (filename.includes('.csv')) {
    link.setAttribute('download', filename);
  } else {
    link.setAttribute('download', filename.concat('.csv'));
  }

  link.setAttribute('href', window.URL.createObjectURL(csvFile));
  link.setAttribute('style.display', 'none');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
